import React, { useState, useEffect, useRef } from 'react';
import { onAllAttendanceChange, exportAttendanceToCSV, autoCheckoutForgotten, fetchAttendanceByDateRange, toDate } from '../services/attendanceDb';
import { 
  MapPin, 
  Clock, 
  FileSpreadsheet, 
  UserCheck, 
  UserMinus, 
  Calendar,
  Filter,
  RefreshCw,
  Search,
  LogOut,
  AlertCircle,
  Download,
  ChevronRight,
  X
} from 'lucide-react';

const COLORS = ['#3b5bdb', '#6d28d9', '#2a9d4f', '#d97706', '#c0392b', '#1e7ec8', '#6b4f9e'];
const CBGS = ['#eef2ff', '#f5f3ff', '#edfaf2', '#fffbeb', '#fef2f2', '#e6f3fc', '#f0edfb'];

function ec(i) { return COLORS[i % COLORS.length]; }
function ecb(i) { return CBGS[i % CBGS.length]; }

const PRESENT_STATUSES = new Set(['IN', 'OUT', 'AUTO_CHECKOUT']);

const parseDateKey = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = (dateStr) => {
  const base = parseDateKey(dateStr) || new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), 1, 12, 0, 0, 0);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0, 12, 0, 0, 0);
  return {
    monthStart: formatDateKey(start),
    monthEnd: formatDateKey(end),
    monthLabel: start.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
  };
};

const getMonthValue = (dateStr) => {
  const date = parseDateKey(dateStr);
  return date ? date.getFullYear() * 12 + date.getMonth() : 0;
};

const getMonthDateKeys = (startStr, endStr) => {
  const start = parseDateKey(startStr);
  const end = parseDateKey(endStr);
  if (!start || !end || start > end) return [];

  const dates = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(formatDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const formatTimeValue = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '--';
};

const formatDayLabel = (dateStr) => {
  const date = parseDateKey(dateStr);
  return date
    ? date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
    : dateStr;
};

const isLateCheckIn = (checkIn, dateStr) => {
  const checkInTime = toDate(checkIn);
  const day = parseDateKey(dateStr);
  if (!checkInTime || !day) return false;
  const lateThreshold = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 30, 0, 0);
  return checkInTime > lateThreshold;
};

const normalizeLeaveStatus = (status) => {
  if (status === 'pending_head') return 'Pending (head)';
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const isActiveLeaveStatus = (status) => status && status !== 'rejected';

const getLeaveDayFraction = (leave, dateStr) => {
  if (!leave?.from || !leave?.to) return 0;
  if (leave.from === leave.to) {
    return leave.leaveHalfSession ? 0.5 : 1;
  }
  if (dateStr === leave.from && leave.leaveHalfFirst) return 0.5;
  if (dateStr === leave.to && leave.leaveHalfLast) return 0.5;
  return 1;
};

const buildLeaveMaps = (employeeLeaves, monthDateKeys) => {
  const monthKeySet = new Set(monthDateKeys);
  const allByDate = {};
  const activeByDate = {};

  (employeeLeaves || []).forEach((leave) => {
    const start = parseDateKey(leave.from);
    const end = parseDateKey(leave.to);
    if (!start || !end || start > end) return;

    const cursor = new Date(start);
    while (cursor <= end) {
      const dateKey = formatDateKey(cursor);
      if (monthKeySet.has(dateKey)) {
        const enriched = {
          ...leave,
          fraction: getLeaveDayFraction(leave, dateKey),
        };
        allByDate[dateKey] = [...(allByDate[dateKey] || []), enriched];
        if (isActiveLeaveStatus(leave.status)) {
          activeByDate[dateKey] = [...(activeByDate[dateKey] || []), enriched];
        }
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  });

  return { allByDate, activeByDate };
};

const formatDuration = (hours) => {
  if (!hours || hours <= 0) return '--';
  const totalSeconds = Math.floor(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

function Attendance({ employees = [], leaves = [], initialData, initialSummary, showToast, showMonthlySection = false }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState(initialData || []);
  const [summary, setSummary] = useState(initialSummary || {
    totalEmployees: 0,
    present: 0,
    absent: 0,
    onLeave: 0,
    attendanceRate: 0
  });
  const [loading, setLoading] = useState(() => !initialData?.length);
  const [selectedDate, setSelectedDate] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
  const [filters, setFilters] = useState({ status: '', department: '', employee: '' });
  const [autoCheckoutLoading, setAutoCheckoutLoading] = useState(false);
  const [monthlyAttendanceData, setMonthlyAttendanceData] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');
  const prevSelectedDateRef = useRef(null);
  const todayStr = React.useMemo(() => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(), []);

  const { monthStart, monthEnd, monthLabel } = React.useMemo(
    () => getMonthBounds(selectedDate),
    [selectedDate]
  );
  const monthSummaryEnd = React.useMemo(() => {
    const selectedMonthValue = getMonthValue(monthStart);
    const todayMonthValue = getMonthValue(todayStr);

    if (selectedMonthValue > todayMonthValue) return null;
    if (selectedMonthValue === todayMonthValue) return todayStr;
    return monthEnd;
  }, [monthEnd, monthStart, todayStr]);
  const monthDateKeys = React.useMemo(
    () => (monthSummaryEnd ? getMonthDateKeys(monthStart, monthSummaryEnd) : []),
    [monthStart, monthSummaryEnd]
  );

  // Merge dashboard props into today's view (same Firestore listener as App; avoids duplicate work).
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    if (selectedDate !== today || !initialData?.length) return;
    setAttendanceData(initialData);
    if (initialSummary) setSummary(initialSummary);
    setLoading(false);
  }, [selectedDate, initialData, initialSummary]);

  // Real-time clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Listener keyed by date only — do not depend on initialData (parent updates used to reset loading and clear rows).
  useEffect(() => {
    const prev = prevSelectedDateRef.current;
    const dateChanged = prev !== null && prev !== selectedDate;
    prevSelectedDateRef.current = selectedDate;

    if (dateChanged) {
      setAttendanceData([]);
      setLoading(true);
    }

    const unsub = onAllAttendanceChange(selectedDate, (data) => {
      setAttendanceData(data.records);
      setSummary(data.summary);
      setLoading(false);
    });
    return () => {
      if (unsub) unsub();
    };
  }, [selectedDate]);

  useEffect(() => {
    if (!showMonthlySection) {
      setMonthlyAttendanceData([]);
      setMonthlyLoading(false);
      return undefined;
    }

    let active = true;
    setMonthlyLoading(true);

    fetchAttendanceByDateRange(monthStart, monthEnd)
      .then((rows) => {
        if (!active) return;
        setMonthlyAttendanceData(rows);
      })
      .catch((error) => {
        console.error('Monthly attendance fetch error:', error);
        if (!active) return;
        setMonthlyAttendanceData([]);
        showToast('Failed to load monthly attendance data', 'err');
      })
      .finally(() => {
        if (active) setMonthlyLoading(false);
      });

    return () => {
      active = false;
    };
  }, [monthStart, monthEnd, showMonthlySection, showToast]);

  // Filter attendance data
  const filteredAttendance = React.useMemo(() => {
    return attendanceData.filter(record => {
      const matchesStatus = !filters.status || 
        (filters.status === 'present' && (record.status === 'IN' || record.status === 'OUT' || record.status === 'AUTO_CHECKOUT')) ||
        (filters.status === 'absent' && record.status === 'ABSENT') ||
        (filters.status === 'in' && record.status === 'IN') ||
        (filters.status === 'out' && (record.status === 'OUT' || record.status === 'AUTO_CHECKOUT'));
      
      const matchesEmployee = !filters.employee || 
        record.userName?.toLowerCase().includes(filters.employee.toLowerCase()) ||
        record.email?.toLowerCase().includes(filters.employee.toLowerCase());
      
      const matchesDepartment = !filters.department || record.department === filters.department;
      
      return matchesStatus && matchesEmployee && matchesDepartment;
    });
  }, [attendanceData, filters]);

  // Get unique departments
  const departments = React.useMemo(() => {
    return [...new Set(attendanceData.map(a => a.department).filter(Boolean))];
  }, [attendanceData]);

  const monthlyEmployeeSummaries = React.useMemo(() => {
    if (!showMonthlySection) return [];

    const employeeMap = new Map();

    employees.forEach((employee) => {
      const email = employee.email || employee.id;
      if (!email) return;
      employeeMap.set(email, {
        email,
        name: employee.name || email.split('@')[0],
        department: employee.department || employee.dept || 'N/A',
      });
    });

    monthlyAttendanceData.forEach((row) => {
      const email = row.email || row.userId;
      if (!email) return;
      if (!employeeMap.has(email)) {
        employeeMap.set(email, {
          email,
          name: row.userName || email.split('@')[0],
          department: row.department || 'N/A',
        });
      }
    });

    return [...employeeMap.values()].map((employee) => {
      const employeeRows = monthlyAttendanceData.filter((row) => {
        const rowEmail = row.email || row.userId;
        return rowEmail === employee.email;
      });
      const attendanceByDate = new Map(
        employeeRows
          .filter((row) => row.date)
          .map((row) => [row.date, row])
      );
      const employeeLeaves = (leaves || []).filter(
        (leave) => leave.eEmail === employee.email || leave.emp === employee.name
      );
      const { allByDate, activeByDate } = buildLeaveMaps(employeeLeaves, monthDateKeys);

      let presentDays = 0;
      let absentDays = 0;
      let lateDays = 0;

      monthDateKeys.forEach((dateKey) => {
        const record = attendanceByDate.get(dateKey);
        if (record && (record.checkIn || PRESENT_STATUSES.has(record.status))) {
          presentDays += 1;
          if (isLateCheckIn(record.checkIn, dateKey)) lateDays += 1;
          return;
        }
        if ((activeByDate[dateKey] || []).length > 0) return;
        absentDays += 1;
      });

      const leaveDays = Object.values(activeByDate).reduce((sum, entries) => {
        const fraction = entries.reduce(
          (max, leaveEntry) => Math.max(max, leaveEntry.fraction || 1),
          0
        );
        return sum + fraction;
      }, 0);

      const timeline = monthDateKeys.map((dateKey) => {
        const record = attendanceByDate.get(dateKey);
        const dayLeaves = allByDate[dateKey] || [];
        const activeLeaves = activeByDate[dateKey] || [];
        const isPresent = !!(record && (record.checkIn || PRESENT_STATUSES.has(record.status)));

        return {
          date: dateKey,
          record: record || null,
          leaves: dayLeaves,
          statusLabel: isPresent
            ? record.status === 'IN'
              ? 'Working'
              : 'Present'
            : activeLeaves.length > 0
              ? 'Leave'
              : 'Absent',
          late: isPresent ? isLateCheckIn(record.checkIn, dateKey) : false,
        };
      });

      return {
        ...employee,
        presentDays,
        absentDays,
        lateDays,
        leaveDays,
        timeline,
      };
    }).sort((a, b) => {
      if (b.presentDays !== a.presentDays) return b.presentDays - a.presentDays;
      if (a.absentDays !== b.absentDays) return a.absentDays - b.absentDays;
      return a.name.localeCompare(b.name);
    });
  }, [employees, leaves, monthDateKeys, monthlyAttendanceData, showMonthlySection]);

  const filteredMonthlySummaries = React.useMemo(() => {
    if (!showMonthlySection) return [];
    return monthlyEmployeeSummaries.filter((employee) => {
      const matchesDepartment = !filters.department || employee.department === filters.department;
      const matchesEmployee = !filters.employee ||
        employee.name?.toLowerCase().includes(filters.employee.toLowerCase()) ||
        employee.email?.toLowerCase().includes(filters.employee.toLowerCase());
      return matchesDepartment && matchesEmployee;
    });
  }, [filters.department, filters.employee, monthlyEmployeeSummaries]);

  const selectedEmployeeSummary = React.useMemo(
    () => showMonthlySection
      ? monthlyEmployeeSummaries.find((employee) => employee.email === selectedEmployeeEmail) || null
      : null,
    [monthlyEmployeeSummaries, selectedEmployeeEmail, showMonthlySection]
  );

  useEffect(() => {
    if (selectedEmployeeEmail && !selectedEmployeeSummary) {
      setSelectedEmployeeEmail('');
    }
  }, [selectedEmployeeEmail, selectedEmployeeSummary]);

  const exportCSV = async () => {
    try {
      setLoading(true);
      const csvContent = await exportAttendanceToCSV(selectedDate, selectedDate);
      
      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `attendance_${selectedDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showToast('Attendance data exported successfully!', 'ok');
    } catch (error) {
      showToast('Failed to export data', 'err');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoCheckout = async () => {
    try {
      setAutoCheckoutLoading(true);
      const result = await autoCheckoutForgotten(selectedDate);
      showToast(result.message, 'ok');
    } catch (error) {
      showToast('Auto-checkout failed', 'err');
    } finally {
      setAutoCheckoutLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      IN: { bg: '#fffbeb', color: '#d97706', text: 'WORKING' },
      OUT: { bg: '#eef2ff', color: '#3b5bdb', text: 'OUT' },
      AUTO_CHECKOUT: { bg: '#eef2ff', color: '#3b5bdb', text: 'OUT' },
      ABSENT: { bg: '#fee2e2', color: '#dc2626', text: 'ABSENT' }
    };
    const badge = badges[status] || badges.ABSENT;
    return (
      <span style={{ 
        padding: '4px 10px', 
        borderRadius: '12px', 
        fontSize: '11px', 
        fontWeight: '600',
        background: badge.bg,
        color: badge.color
      }}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="pg">
      {/* Header */}
      <div className="ph">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MapPin size={24} /> Attendance Monitor
          </h1>
          <p>Real-time attendance tracking with live updates</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ textAlign: 'right', marginRight: '10px' }}>
            <div style={{ fontSize: '20px', fontWeight: '600' }}>
              {currentTime.toLocaleTimeString()}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text2)' }}>
              {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
          <button 
            className="btn btn-g" 
            onClick={exportCSV} 
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="sg">
        <div className="sc gn">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="sl">Present Today</div>
              <div className="sv">{summary.present}</div>
            </div>
            <div className="ic-box gn"><UserCheck size={20} /></div>
          </div>
          <div className="ss">{summary.totalEmployees > 0 ? Math.round((summary.present / summary.totalEmployees) * 100) : 0}% attendance rate</div>
        </div>
        <div className="sc rd">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="sl">Absent</div>
              <div className="sv">{summary.absent}</div>
            </div>
            <div className="ic-box rd"><UserMinus size={20} /></div>
          </div>
          <div className="ss">Not checked in</div>
        </div>
        <div className="sc am">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="sl">Checked out</div>
              <div className="sv">
                {attendanceData.filter((a) => a.status === 'OUT' || a.status === 'AUTO_CHECKOUT').length}
              </div>
            </div>
            <div className="ic-box am"><Clock size={20} /></div>
          </div>
          <div className="ss">Finished shift today</div>
        </div>
        <div className="sc bl">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="sl">Currently WORKING</div>
              <div className="sv">{attendanceData.filter(a => a.status === 'IN').length}</div>
            </div>
            <div className="ic-box bl"><RefreshCw size={20} /></div>
          </div>
          <div className="ss">Live tracking</div>
        </div>
      </div>

      {/* Filters & Controls */}
      <div className="card attendance-filters-card" style={{ marginBottom: '20px', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
          <Filter size={18} color="var(--text2)" />
          <span style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Filters</span>
        </div>

        <div className="attendance-filter-grid">
          <label className="attendance-filter-field">
            <span className="attendance-filter-label">Date</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={16} color="var(--text2)" style={{ flexShrink: 0 }} />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="attendance-filter-input"
              />
            </div>
          </label>

          <label className="attendance-filter-field">
            <span className="attendance-filter-label">Status</span>
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              className="attendance-filter-input"
            >
              <option value="">All Status</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
              <option value="in">Working</option>
              <option value="out">Out</option>
            </select>
          </label>

          <label className="attendance-filter-field">
            <span className="attendance-filter-label">Department</span>
            <select
              value={filters.department}
              onChange={(e) => setFilters((prev) => ({ ...prev, department: e.target.value }))}
              className="attendance-filter-input"
            >
              <option value="">All Departments</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="attendance-filter-row2">
          <label className="attendance-filter-field attendance-filter-search">
            <span className="attendance-filter-label">Search employee</span>
            <div style={{ position: 'relative', width: '100%' }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text3)',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                placeholder="Name or email..."
                value={filters.employee}
                onChange={(e) => setFilters((prev) => ({ ...prev, employee: e.target.value }))}
                className="attendance-filter-input"
                style={{ paddingLeft: '36px' }}
              />
            </div>
          </label>
          <div className="attendance-filter-actions">
            <button
              type="button"
              className="btn btn-g"
              onClick={handleAutoCheckout}
              disabled={autoCheckoutLoading}
              title="Auto-checkout users who forgot to check out"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                padding: '8px 14px',
                whiteSpace: 'nowrap',
                height: '38px',
                boxSizing: 'border-box',
              }}
            >
              <LogOut size={14} />
              {autoCheckoutLoading ? 'Processing...' : 'Auto Checkout'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .attendance-filter-label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: var(--text2);
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .attendance-filter-input {
          width: 100%;
          box-sizing: border-box;
          padding: 8px 12px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 13px;
          background: var(--surface, #fff);
          color: var(--text);
          min-height: 38px;
        }
        .attendance-filter-input:focus {
          outline: none;
          border-color: var(--accent, #3b5bdb);
          box-shadow: 0 0 0 2px rgba(59, 91, 219, 0.12);
        }
        .attendance-filter-field {
          display: flex;
          flex-direction: column;
          margin: 0;
          min-width: 0;
        }
        .attendance-filter-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px 16px;
          align-items: end;
        }
        .attendance-filter-row2 {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 12px 16px;
          align-items: end;
          margin-top: 12px;
          padding-top: 4px;
          border-top: 1px solid var(--border);
        }
        .attendance-filter-search {
          min-width: 0;
        }
        .attendance-filter-actions {
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          justify-content: flex-end;
          padding-top: 22px;
        }
        @media (max-width: 900px) {
          .attendance-filter-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 768px) {
          .attendance-filter-grid {
            grid-template-columns: 1fr;
          }
          .attendance-filter-row2 {
            grid-template-columns: 1fr;
          }
          .attendance-filter-actions {
            padding-top: 0 !important;
          }
          .attendance-filter-actions button {
            width: 100%;
            justify-content: center;
          }
          .table-container { margin: 0 -15px !important; border-radius: 0 !important; border-left: none; border-right: none; }
        }
        .attendance-month-row:hover {
          background: #fafbff;
        }
        .attendance-month-detail-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }
        .attendance-month-stat {
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 14px;
          background: var(--surface, #fff);
        }
        .attendance-month-stat-label {
          font-size: 11px;
          font-weight: 700;
          color: var(--text2);
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 6px;
        }
        .attendance-month-stat-value {
          font-size: 22px;
          font-weight: 700;
          color: var(--text);
        }
        .attendance-timeline-table {
          min-width: 900px;
          width: 100%;
          border-collapse: collapse;
        }
        @media (max-width: 900px) {
          .attendance-month-detail-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 640px) {
          .attendance-month-detail-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Live Attendance Table */}
      <div className="card" style={{ padding: '0' }}>
        <div style={{ 
          padding: '16px 20px', 
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RefreshCw size={18} /> 
            Attendance Log 
            <span style={{ 
              padding: '4px 10px', 
              background: '#dcfce7', 
              color: '#16a34a',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              LIVE
            </span>
          </h3>
          <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
            {filteredAttendance.length} of {attendanceData.length} records
          </span>
        </div>
        
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ minWidth: '720px', width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-sec)' }}>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>EMPLOYEE</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>DEPARTMENT</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>CHECK IN</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>CHECK OUT</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>HOURS</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {loading && attendanceData.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--text2)' }}>
                    <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
                    <div>Loading attendance data...</div>
                  </td>
                </tr>
              ) : filteredAttendance.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--text2)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                      <AlertCircle size={32} style={{ color: 'var(--text3)' }} />
                      <span>No attendance records found</span>
                      <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
                        Try adjusting your filters or select a different date
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAttendance
                  .sort((a, b) => (b.checkIn ? 1 : 0) - (a.checkIn ? 1 : 0))
                  .map((record, index) => (
                  <tr key={record.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div 
                          className="av" 
                          style={{ 
                            background: ecb(index), 
                            color: ec(index), 
                            width: '32px', 
                            height: '32px', 
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            fontWeight: '600'
                          }}
                        >
                          {record.userName?.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>{record.userName}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{record.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                      {record.department}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                      {record.checkIn ? 
                        (toDate(record.checkIn)?.toLocaleTimeString() || '--') 
                        : '--'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                      {record.checkOut ? 
                        (toDate(record.checkOut)?.toLocaleTimeString() || '--') 
                        : (record.status === 'IN' ? 
                          <span style={{ color: '#d97706' }}>In Progress...</span> : '--')}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600' }}>
                      {record.totalHours > 0 ? (
                        <span style={{ color: record.totalHours >= 9 ? '#2a9d4f' : '#d97706' }}>
                          {formatDuration(record.totalHours)}
                        </span>
                      ) : '--'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {getStatusBadge(record.status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {!loading && filteredAttendance.length > 0 && (
          <div style={{ 
            padding: '12px 20px', 
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: 'var(--text3)'
          }}>
            <span>
              Absent: {filteredAttendance.filter(a => a.status === 'ABSENT').length}
            </span>
            <span>Last updated: {currentTime.toLocaleTimeString()}</span>
          </div>
        )}
      </div>

      {showMonthlySection && (
      <div className="card" style={{ padding: '0', marginTop: '20px' }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileSpreadsheet size={18} />
              Monthly Employee Summary
            </h3>
            <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '4px' }}>
              {monthLabel} ke liye present, absent, late aur leave day summary
              {monthSummaryEnd === todayStr ? ' (till today)' : ''}
            </div>
          </div>
          <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
            {filteredMonthlySummaries.length} employees
          </span>
        </div>

        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ minWidth: '760px', width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-sec)' }}>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>EMPLOYEE</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>DEPARTMENT</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>PRESENT</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>ABSENT</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>LATE</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>LEAVE</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'right' }}>DETAIL</th>
              </tr>
            </thead>
            <tbody>
              {monthlyLoading ? (
                <tr>
                  <td colSpan="7" style={{ padding: '32px', textAlign: 'center', color: 'var(--text2)' }}>
                    <RefreshCw size={22} style={{ animation: 'spin 1s linear infinite', marginBottom: '10px' }} />
                    <div>Loading monthly summary...</div>
                  </td>
                </tr>
              ) : filteredMonthlySummaries.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '32px', textAlign: 'center', color: 'var(--text2)' }}>
                    No monthly employee summary found for this filter.
                  </td>
                </tr>
              ) : (
                filteredMonthlySummaries.map((employee, index) => {
                  const isOpen = selectedEmployeeEmail === employee.email;
                  return (
                    <tr
                      key={employee.email}
                      className="attendance-month-row"
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                      onClick={() => setSelectedEmployeeEmail(isOpen ? '' : employee.email)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedEmployeeEmail(isOpen ? '' : employee.email);
                        }
                      }}
                      tabIndex={0}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div
                            className="av"
                            style={{
                              background: ecb(index),
                              color: ec(index),
                              width: '32px',
                              height: '32px',
                              fontSize: '11px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                              fontWeight: '600'
                            }}
                          >
                            {employee.name?.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px' }}>{employee.name}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{employee.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{employee.department || 'N/A'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', textAlign: 'center', fontWeight: '600', color: '#2a9d4f' }}>{employee.presentDays}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', textAlign: 'center', fontWeight: '600', color: '#c0392b' }}>{employee.absentDays}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', textAlign: 'center', fontWeight: '600', color: '#d97706' }}>{employee.lateDays}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>{employee.leaveDays || 0}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--p)', fontSize: '12px', fontWeight: '600' }}>
                          {isOpen ? 'Hide details' : 'View details'}
                          <ChevronRight size={14} style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {showMonthlySection && selectedEmployeeSummary && (
        <div className="card" style={{ marginTop: '20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            <div>
              <h3 style={{ margin: 0 }}>{selectedEmployeeSummary.name} - {monthLabel}</h3>
              <div style={{ fontSize: '13px', color: 'var(--text2)', marginTop: '6px' }}>
                {selectedEmployeeSummary.email} {selectedEmployeeSummary.department ? `· ${selectedEmployeeSummary.department}` : ''}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-g"
              onClick={() => setSelectedEmployeeEmail('')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <X size={14} />
              Close
            </button>
          </div>

          <div className="attendance-month-detail-grid" style={{ marginBottom: '16px' }}>
            <div className="attendance-month-stat">
              <div className="attendance-month-stat-label">Present Days</div>
              <div className="attendance-month-stat-value" style={{ color: '#2a9d4f' }}>{selectedEmployeeSummary.presentDays}</div>
            </div>
            <div className="attendance-month-stat">
              <div className="attendance-month-stat-label">Absent Days</div>
              <div className="attendance-month-stat-value" style={{ color: '#c0392b' }}>{selectedEmployeeSummary.absentDays}</div>
            </div>
            <div className="attendance-month-stat">
              <div className="attendance-month-stat-label">Late Days</div>
              <div className="attendance-month-stat-value" style={{ color: '#d97706' }}>{selectedEmployeeSummary.lateDays}</div>
            </div>
            <div className="attendance-month-stat">
              <div className="attendance-month-stat-label">Leave Days</div>
              <div className="attendance-month-stat-value">{selectedEmployeeSummary.leaveDays || 0}</div>
            </div>
          </div>

          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid var(--border)', borderRadius: '12px' }}>
            <table className="attendance-timeline-table">
              <thead>
                <tr style={{ background: 'var(--bg-sec)' }}>
                  <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>DATE</th>
                  <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>CHECK IN</th>
                  <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>CHECK OUT</th>
                  <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>HOURS</th>
                  <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>STATUS</th>
                  <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>LEAVE</th>
                </tr>
              </thead>
              <tbody>
                {selectedEmployeeSummary.timeline.map((day) => (
                  <tr key={day.date} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600' }}>{formatDayLabel(day.date)}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{formatTimeValue(day.record?.checkIn)}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                      {day.record?.status === 'IN' && !day.record?.checkOut ? (
                        <span style={{ color: '#d97706' }}>In Progress...</span>
                      ) : (
                        formatTimeValue(day.record?.checkOut)
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600' }}>
                      {day.record?.totalHours > 0 ? formatDuration(day.record.totalHours) : '--'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {day.record ? getStatusBadge(day.record.status) : (
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: day.statusLabel === 'Leave' ? '#eef2ff' : '#fee2e2',
                            color: day.statusLabel === 'Leave' ? '#3b5bdb' : '#dc2626'
                          }}>
                            {day.statusLabel.toUpperCase()}
                          </span>
                        )}
                        {day.late && (
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '10px',
                            fontWeight: '700',
                            background: '#fffbeb',
                            color: '#d97706'
                          }}>
                            LATE
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text2)' }}>
                      {day.leaves.length === 0 ? (
                        '--'
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {day.leaves.map((leaveEntry) => (
                            <div key={`${leaveEntry.id || leaveEntry.from}-${leaveEntry.status}-${day.date}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: '600', color: 'var(--text)' }}>{leaveEntry.type || 'Leave'}</span>
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: '999px',
                                fontSize: '10px',
                                fontWeight: '700',
                                background: leaveEntry.status === 'approved' ? '#edfaf2' : leaveEntry.status === 'rejected' ? '#fef2f2' : '#fffbeb',
                                color: leaveEntry.status === 'approved' ? '#2a9d4f' : leaveEntry.status === 'rejected' ? '#c0392b' : '#b45309'
                              }}>
                                {normalizeLeaveStatus(leaveEntry.status)}
                              </span>
                              <span>
                                {leaveEntry.fraction === 0.5 ? 'Half day' : 'Full day'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Attendance;
